export const LINKS_MAX = 1000
export const LINK_MAX_LENGTH = 256

export const LINK_FORMAT_ERROR =
  "Ссылка должна быть @username (5–32 символа: латиница, цифры, „_“; начинается с буквы), публичной ссылкой t.me/<name> или инвайт-ссылкой"
export const LINK_LENGTH_ERROR = "Ссылка длиннее 256 символов"
export const LINKS_COUNT_ERROR = "Нужно от 1 до 1000 ссылок"
export const LINK_DUPLICATE_ERROR = "Дубликат ссылки"

export type LinkParseResult =
  | { ok: true; key: string; kind: "username" | "invite"; link: string }
  | { ok: false; error: string }

// @username: @ + 5..32 chars of [A-Za-z0-9_], first char a latin letter
export const USERNAME_RE = /^@[A-Za-z][A-Za-z0-9_]{4,31}$/
// t.me/+<hash> or t.me/joinchat/<hash> (also telegram.me|t.dog|t.org),
// optional https?:// and www.
export const INVITE_RE =
  /^(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me|t\.dog|t\.org)\/(?:\+|joinchat\/)([A-Za-z0-9_-]+)$/
// tg://join?invite=<hash>
export const TG_JOIN_RE = /^tg:\/\/join\?invite=([A-Za-z0-9_-]+)$/
// t.me/<username> public chat link (same hosts and prefixes as invites).
export const PUBLIC_LINK_RE =
  /^(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me|t\.dog|t\.org)\/([A-Za-z][A-Za-z0-9_]{4,31})$/

export const parseCampaignLink = (raw: string): LinkParseResult => {
  const value = raw.trim()
  const tgJoin = TG_JOIN_RE.exec(value)
  if (tgJoin) {
    return { ok: true, key: `i:${tgJoin[1]}`, kind: "invite", link: value }
  }
  const invite = INVITE_RE.exec(value)
  if (invite) {
    return { ok: true, key: `i:${invite[1]}`, kind: "invite", link: value }
  }
  const publicLink = PUBLIC_LINK_RE.exec(value)
  if (publicLink) {
    const name = publicLink[1]
    return {
      ok: true,
      key: `u:${name.toLowerCase()}`,
      kind: "username",
      // API grammar takes @username, not t.me/<name>.
      link: `@${name}`,
    }
  }
  if (USERNAME_RE.test(value)) {
    return {
      ok: true,
      key: `u:${value.slice(1).toLowerCase()}`,
      kind: "username",
      link: value,
    }
  }
  return { ok: false, error: LINK_FORMAT_ERROR }
}

export interface CampaignLinksValidation {
  /** Deduplicated valid links (first occurrence kept), in input order. */
  links: string[]
  /**
   * index === -1 is the form-level links-count error; otherwise the RAW line
   * index (blank lines keep their slots — matches the wizard's raw rendering).
   */
  errors: { index: number; message: string }[]
  duplicateIndexes: number[]
}

export const validateCampaignInput = (raw: string): CampaignLinksValidation => {
  // Raw split with original indexes: the wizard renders `raw.split(/\r?\n/)`,
  // so error/duplicate indexes must point at RAW line numbers — otherwise an
  // error on a line below a blank one lands on the wrong (or blank) row.
  const lines = raw.split(/\r?\n/)

  const errors: { index: number; message: string }[] = []
  const nonBlankCount = lines.filter((line) => line.trim() !== "").length
  if (nonBlankCount < 1 || nonBlankCount > LINKS_MAX) {
    errors.push({ index: -1, message: LINKS_COUNT_ERROR })
  }

  const links: string[] = []
  const duplicateIndexes: number[] = []
  const seen = new Map<string, number>()

  lines.forEach((line, index) => {
    const trimmed = line.trim()
    // Blank lines are neither validated nor counted (1..1000 applies to links).
    if (trimmed === "") return
    if (trimmed.length > LINK_MAX_LENGTH) {
      errors.push({ index, message: LINK_LENGTH_ERROR })
      return
    }
    const parsed = parseCampaignLink(trimmed)
    if (!parsed.ok) {
      errors.push({ index, message: parsed.error })
      return
    }
    if (seen.has(parsed.key)) {
      duplicateIndexes.push(index)
      return
    }
    seen.set(parsed.key, index)
    links.push(parsed.link)
  })

  return { links, errors, duplicateIndexes }
}
